import type { PaginateProps, PaginateReturn, PaginateResponse, Response } from 'types';
import { Response as MirageResponse } from 'miragejs';
import { HTTP_RESPONSE_CODES } from 'mocks/constants';

type Attribs = {
  statusCode?: number;
  headers?: Record<string, string>;
  status?: string;
  code?: string;
  pagination?: { cursor: string; hasMore: boolean };
  error?: { details: { type: string; value: string }[] };
  body?: unknown | unknown[];
};

type Transform = {
  statusCode?: number;
  data?: unknown | unknown[];
  code?: string;
  error?: { details: { type: string; value: string }[] };
  url: string;
};

/**
 * Creates a mock API response with our standardized response format
 * @param {string} url - The URL endpoint for the response (request.url from mock server callback)
 * @param {unknown | unknown[]} body - The response body data (will be used in the data field of the response)
 * @param {Object} [attribs] - Optional attributes for customizing the response
 * @param {number} [attribs.statusCode] - HTTP status code for the response (defaults to 200)
 * @param {Record<string, string>} [attribs.headers] - Custom headers to include in the response (defaults to JSON content type)
 * @param {string} [attribs.status] - Response status ('success' or 'fail', success is default if statusCode is below 400)
 * @param {string} [attribs.code] - Response code (defaults to 'ok'), used to look up error message (see design doc for existing codes)
 * @param {string} [attribs.pagination.cursor] - Pagination cursor
 * @param {boolean} [attribs.pagination.hasMore] - Whether there are more pages
 * @param {Object} [attribs.error] - Error details (defaults to null)
 * @param {unknown | unknown[]} [attribs.body] - Custom response body (overrides the entire body), only used for special cases (and for support for old mocks)
 */
export const response = (url: string, body: unknown | unknown[], attribs?: Attribs) => {
  const current = localStorage.getItem('api-transform');
  let transform: Transform = { url: '' };
  if (current) {
    try {
      transform = JSON.parse(current || '{}');
    } catch (error) {
      void 0;
    }
  }
  let status = 'success';
  if (attribs?.status) {
    status = attribs.status;
  } else if (attribs?.statusCode) {
    status = attribs.statusCode < 400 ? 'success' : 'fail';
  }
  let code = attribs?.code ?? 'ok';
  let error = attribs?.error ?? null;
  let statusCode = attribs?.statusCode ?? HTTP_RESPONSE_CODES.OK;
  let data = body;

  if (transform.url) {
    if (url.includes(transform.url)) {
      if (transform.statusCode) {
        statusCode = transform.statusCode;
      }
      if (transform.data) {
        data = transform.data;
      }
      if (transform.code) {
        code = transform.code;
      }
      if (transform.error) {
        error = transform.error;
      }
    }
  }

  if (attribs?.pagination) {
    const responseBody: PaginateResponse<unknown> = {
      status,
      code,
      data: Array.isArray(data) ? data : [data],
      pagination: attribs?.pagination ?? { cursor: '', hasMore: false },
      error,
      statusCode,
    };
    return new MirageResponse(statusCode, attribs?.headers ?? undefined, responseBody);
  }
  if (attribs?.body) {
    return new MirageResponse(statusCode, attribs?.headers ?? undefined, attribs?.body);
  }
  const responseBody: Response<unknown> = {
    status,
    code,
    data,
    error,
    statusCode,
  };
  return new MirageResponse(statusCode, attribs?.headers ?? undefined, responseBody);
};

export const paginate = <T>({
  items = [],
  perPage = 20,
  page = 1,
}: PaginateProps<T>): PaginateReturn<T> => {
  const matrix: T[][] = items.reduce<T[][]>((acc: T[][], item: T, index: number) => {
    const pageIndex = Math.floor(index / perPage);

    if (!acc[pageIndex]) {
      acc[pageIndex] = [];
    }

    acc[pageIndex].push(item);

    return acc;
  }, []);

  const validPage = page - 1 < matrix.length && page > 0 ? page - 1 : 0;
  const pageItems = matrix[validPage] || [];

  return {
    pageItems,
    pageSize: pageItems.length,
    numberOfPages: matrix.length,
  };
};

export const range = (len: number) => {
  const arr: number[] = [];
  for (let i = 0; i < len; i++) {
    arr.push(i);
  }
  return arr;
};

export function makeData<T>(len: number, fakeObject: (identifyer: number) => T) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(index),
    };
  });
}

export function makeChildData<T>(
  currentParentId: number,
  len: number,
  fakeObject: (parentId: number, identifyer: number) => T
) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(currentParentId, index),
    };
  });
}

export function makeFacilitiesData<T>(
  organizationSubSupplierId: string,
  len: number,
  fakeObject: (orgubSupplierId: string, identifyer: number) => T
) {
  return range(len).map((_, index): T => {
    return {
      ...fakeObject(organizationSubSupplierId, index),
    };
  });
}
